/**
 * @fileoverview Reddit integration content for the Integrations page.
 * @module reddit-connect/components/RedditIntegrationContent
 */

import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import RedditIcon from '@mui/icons-material/Reddit';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTheme } from '@mui/material/styles';
import { useRedditConnect } from '../hooks/useRedditConnect';

export interface RedditIntegrationContentProps {
  projectId: string;
  showHeader?: boolean;
}

function formatDate(value?: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

export function RedditIntegrationContent({
  projectId,
  showHeader = false,
}: RedditIntegrationContentProps) {
  const theme = useTheme();
  const {
    status,
    isConnected,
    loading,
    actionLoading,
    error,
    connect,
    disconnect,
    refreshStatus,
  } = useRedditConnect(projectId);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  const connectedAt = formatDate(status?.connectedAt);
  const expiresAt = formatDate(status?.accessTokenExpiresAt);

  return (
    <Box>
      {showHeader && (
        <>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            Reddit Integration
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Connect a Reddit account for official Data API access.
          </Typography>
        </>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isConnected ? (
        <Paper variant="outlined" sx={{ p: 3 }}>
          {status?.needsReconnect && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>Reconnect required</AlertTitle>
              The stored Reddit authorization can no longer be refreshed.
            </Alert>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
            <RedditIcon color="action" sx={{ fontSize: 40 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Connected as u/{status?.username}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {status?.displayName || status?.redditUserId || 'Reddit account connected'}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}>
                {(status?.scopes || []).map((scope) => (
                  <Chip key={scope} size="small" label={scope} variant="outlined" />
                ))}
              </Stack>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={refreshStatus}
                disabled={actionLoading}
              >
                Refresh
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={actionLoading ? <CircularProgress size={18} color="inherit" /> : <LinkOffIcon />}
                onClick={disconnect}
                disabled={actionLoading}
              >
                Disconnect
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ mt: 2, display: 'grid', gap: 1 }}>
            {connectedAt && (
              <Typography variant="body2" color="text.secondary">
                Connected: {connectedAt}
              </Typography>
            )}
            {expiresAt && (
              <Typography variant="body2" color="text.secondary">
                Access token expires: {expiresAt}
              </Typography>
            )}
            {status?.lastRateLimit?.remaining != null && (
              <Typography variant="body2" color="text.secondary">
                Reddit API remaining: {status.lastRateLimit.remaining}
              </Typography>
            )}
          </Box>
        </Paper>
      ) : (
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            textAlign: 'center',
            borderColor: theme.palette.divider,
            borderStyle: 'dashed',
          }}
        >
          <RedditIcon color="action" sx={{ fontSize: 56, mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            Connect Reddit
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 3, maxWidth: 520, mx: 'auto' }}
          >
            Authorize Segmently with Reddit read/history/submit access. Tokens are stored in Google Secret Manager.
          </Typography>
          <Button
            variant="contained"
            startIcon={actionLoading ? <CircularProgress size={20} color="inherit" /> : <RedditIcon />}
            onClick={connect}
            disabled={actionLoading}
          >
            {actionLoading ? 'Connecting...' : 'Connect Reddit Account'}
          </Button>
          <Alert severity="info" sx={{ mt: 3, textAlign: 'left' }}>
            <AlertTitle>Required Reddit app setup</AlertTitle>
            <Typography variant="body2">
              Configure the Reddit provider credentials and callback URL in Admin before connecting.
            </Typography>
          </Alert>
        </Paper>
      )}
    </Box>
  );
}

export default RedditIntegrationContent;

